import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createParticipant,
  getMeeting,
  getMeetingPrep,
  GET_MEETING_PREP,
  MEETING_GET,
} from "@/api";
import { Client, MeetingInvitee, Participant } from "@/types";
import { getPrimaryEmail } from "@/lib";
import { useCallback } from "react";

export const useCreateParticipantsFromMeeting = (meetingId?: string | null) => {
  const { data: meeting } = useQuery({
    queryKey: [MEETING_GET, meetingId],
    queryFn: () => getMeeting(meetingId, { includeInvitees: true }),
    enabled: !!meetingId,
  });

  const { data: meetingPrep } = useQuery({
    queryKey: [GET_MEETING_PREP, meeting?.meetingPrepId],
    queryFn: () => getMeetingPrep(meeting?.meetingPrepId),
    enabled: !!meeting?.meetingPrepId,
  });

  const { mutate: onCreateParticipant } = useMutation({
    mutationFn: (data: Partial<Participant>) => createParticipant(data),
  });

  const onCreateParticipants = useCallback(
    async (
      conversationId: string,
      editedParticipants?: Partial<Participant>[],
    ) => {
      // When the caller supplies the
      // final reviewed list — create exactly those, even when empty.
      if (editedParticipants !== undefined) {
        editedParticipants.forEach((draft) => {
          const participant: Partial<Participant> = {
            conversationId: conversationId,
            email: draft.email,
            name: draft.name || "",
          };
          if (draft.userId) {
            participant.userId = draft.userId;
          }
          if (draft.clientId) {
            participant.clientId = draft.clientId;
          }
          onCreateParticipant(participant);
        });
        return;
      }

      const invitees = meeting?.invitees ?? [];
      const prepClients = meetingPrep?.clients ?? [];

      if (!invitees.length && !prepClients.length) return;

      const seenClientIds = new Set<string>();
      const seenEmails = new Set<string>();

      invitees.forEach((invitee: MeetingInvitee) => {
        const participant: Partial<Participant> = {
          conversationId: conversationId,
          email: invitee.email,
          name: invitee.name || "",
        };
        if (invitee.userId) {
          participant.userId = invitee.userId;
        }
        if (invitee.clientId) {
          participant.clientId = invitee.clientId;
          seenClientIds.add(invitee.clientId);
        }
        if (invitee.email) {
          seenEmails.add(invitee.email.toLowerCase());
        }
        onCreateParticipant(participant);
      });

      prepClients.forEach((client: Client) => {
        if (seenClientIds.has(client.id)) return;
        const primaryEmail = getPrimaryEmail(client);
        const normalizedEmail = primaryEmail.toLowerCase();
        if (normalizedEmail && seenEmails.has(normalizedEmail)) return;

        const participant: Partial<Participant> = {
          conversationId: conversationId,
          email: primaryEmail,
          name: `${client.firstname ?? ""} ${client.lastname ?? ""}`.trim(),
          clientId: client.id,
        };
        onCreateParticipant(participant);

        seenClientIds.add(client.id);
        if (normalizedEmail) seenEmails.add(normalizedEmail);
      });
    },
    [meeting?.invitees, meetingPrep?.clients, onCreateParticipant],
  );

  return {
    onCreateParticipants,
    meeting,
  };
};
